import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, User, HelpCircle, Loader2, Info, Compass, Waves, CloudSun, ExternalLink, RefreshCw } from 'lucide-react';
import { chatWithAssistant, getDetailedWeather } from '../services/geminiService';
import { ChatMessage } from '../types';

const ChatAssistant: React.FC = () => {
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isWeatherOpen, setIsWeatherOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([
    { role: 'model', text: 'Hello! I am your local island guide. How can I help you plan your trip to Balut Island today?' }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [weatherData, setWeatherData] = useState<{text: string, sources: any[]} | null>(null);
  const [isWeatherLoading, setIsWeatherLoading] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isLoading]);

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;

    const userMsg = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', text: userMsg }]);
    setIsLoading(true);

    try {
      const response = await chatWithAssistant(messages, userMsg);
      setMessages(prev => [...prev, { role: 'model', text: response }]);
    } catch (err) {
      setMessages(prev => [...prev, { role: 'model', text: "I'm having a bit of trouble connecting. Please try again in a moment." }]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchQuickWeather = async () => {
    setIsWeatherLoading(true);
    const data = await getDetailedWeather();
    setWeatherData(data);
    setIsWeatherLoading(false);
  };

  const toggleWeather = () => {
    if (!isWeatherOpen && !weatherData) {
      fetchQuickWeather();
    }
    setIsWeatherOpen(!isWeatherOpen);
    if (isChatOpen) setIsChatOpen(false);
  };

  const toggleChat = () => {
    setIsChatOpen(!isChatOpen);
    if (isWeatherOpen) setIsWeatherOpen(false);
  };

  return (
    <div className="fixed bottom-6 right-6 md:bottom-8 md:right-8 z-[80] flex flex-col items-end gap-4">
      
      {isWeatherOpen && (
        <div className="absolute bottom-24 right-0 w-72 p-6 bg-white rounded-[2rem] shadow-2xl border border-slate-100 animate-in slide-in-from-bottom-4 fade-in duration-300">
          <div className="flex items-center justify-between mb-4">
            <span className="text-[9px] font-bold uppercase tracking-luxury text-sky-500">Current Forecast</span>
            <button onClick={fetchQuickWeather} className="text-slate-300 hover:text-sky-500">
              <RefreshCw className={`w-3 h-3 ${isWeatherLoading ? 'animate-spin' : ''}`} />
            </button>
          </div>
          {isWeatherLoading ? (
             <div className="py-4 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-sky-100" /></div>
          ) : (
            <>
              <p className="text-slate-700 font-serif italic text-sm leading-relaxed mb-4 whitespace-pre-wrap">
                {weatherData?.text?.replace(/\*\*/g, '') || "Checking conditions..."}
              </p>
              
              {!isWeatherLoading && weatherData?.sources && weatherData.sources.length > 0 && (
                <div className="mb-4 space-y-2 border-t border-slate-50 pt-3">
                  <span className="text-[8px] font-bold text-slate-400 uppercase tracking-widest block mb-2">Sources</span>
                  {weatherData.sources.map((chunk: any, i: number) => {
                    const web = chunk.web || chunk.search_entry;
                    if (!web?.uri) return null;
                    return (
                      <a 
                        key={i} 
                        href={web.uri} 
                        target="_blank" 
                        rel="noreferrer" 
                        className="flex items-center gap-2 text-[9px] text-slate-400 hover:text-sky-500 transition-colors"
                      >
                        <ExternalLink className="w-3 h-3" />
                        <span className="truncate">{web.title || 'Official Update'}</span>
                      </a>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {isChatOpen && (
        <div className="absolute bottom-24 right-0 w-[90vw] md:w-[450px] h-[600px] glass-card rounded-[3rem] editorial-shadow overflow-hidden flex flex-col animate-in slide-in-from-bottom-12 fade-in duration-700 border-white/40">
          <div className="bg-slate-900 p-8 text-white relative overflow-hidden">
            <div className="relative z-10 flex items-center justify-between">
              <div>
                <span className="text-[9px] font-bold uppercase tracking-luxury text-sky-400 mb-2 block">Tourism Office</span>
                <h4 className="text-2xl font-serif italic font-bold">Local Guide</h4>
              </div>
              <div className="p-3 bg-white/10 rounded-2xl backdrop-blur-md">
                <HelpCircle className="w-5 h-5 text-sky-400" />
              </div>
            </div>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-8 space-y-6 no-scrollbar bg-white/40">
            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`flex flex-col max-w-[85%] ${msg.role === 'user' ? 'items-end' : 'items-start'}`}>
                  <div className={`p-5 rounded-[1.5rem] text-sm leading-relaxed ${msg.role === 'user' ? 'bg-slate-900 text-white rounded-tr-none' : 'bg-white shadow-sm border border-slate-100 text-slate-700 rounded-tl-none font-serif italic text-base'}`}>
                    {msg.text}
                  </div>
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-white/50 backdrop-blur-sm p-4 rounded-2xl rounded-tl-none border border-white/50">
                  <div className="flex gap-1">
                    <div className="w-1 h-1 bg-sky-400 rounded-full animate-bounce"></div>
                    <div className="w-1 h-1 bg-sky-400 rounded-full animate-bounce [animation-delay:0.2s]"></div>
                    <div className="w-1 h-1 bg-sky-400 rounded-full animate-bounce [animation-delay:0.4s]"></div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="p-8 bg-white/80 backdrop-blur-xl border-t border-white/20">
            <div className="relative group">
              <input 
                type="text" 
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSend()}
                placeholder="Ask about travel, boats, or sights..."
                className="w-full pl-6 pr-16 py-4 rounded-xl bg-slate-50 border border-slate-100 text-sm focus:outline-none focus:border-sky-500 transition-all placeholder:text-slate-300"
              />
              <button 
                onClick={handleSend}
                disabled={isLoading}
                className="absolute right-2 top-2 p-2.5 bg-slate-900 text-white rounded-lg hover:bg-sky-500 transition-all disabled:opacity-50"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-col gap-3">
        <button 
          onClick={toggleWeather}
          className={`w-12 h-12 md:w-14 md:h-14 rounded-full shadow-2xl flex items-center justify-center transition-all duration-500 transform hover:scale-110 active:scale-95 group border border-slate-100/10 ${isWeatherOpen ? 'bg-sky-500 text-white' : 'bg-slate-900 text-white md:bg-white md:text-slate-900'}`}
        >
          <Waves className={`w-5 h-5 md:w-6 md:h-6 transition-transform duration-700 ${isWeatherOpen ? 'rotate-180' : 'group-hover:translate-y-[-2px]'}`} strokeWidth={1.5} />
        </button>

        <button 
          onClick={toggleChat}
          className={`w-12 h-12 md:w-14 md:h-14 rounded-full shadow-2xl flex items-center justify-center transition-all duration-500 transform hover:scale-110 active:scale-95 group border border-slate-100/10 ${isChatOpen ? 'bg-slate-900 text-white' : 'bg-slate-900 text-white md:bg-white md:text-slate-900'}`}
        >
          {isChatOpen ? (
            <X className="w-5 h-5 md:w-6 md:h-6" />
          ) : (
            <MessageCircle className="w-6 h-6 md:w-7 md:h-7 group-hover:scale-110 transition-transform duration-700" strokeWidth={1.5} />
          )}
        </button>
      </div>
    </div>
  );
};

export default ChatAssistant;